import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from "react-router-dom";
import DropdownMenu from './DropdownMenu';
import { useTheme } from './ThemeContext';
import './RankingsPage.css';


const backendUrl = process.env.REACT_APP_BACKEND_URL;

const RankingsPage = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const highlightBg = theme === 'dark' ? 'rgba(0, 245, 196, 0.18)' : '#4CAF50';
  const highlightColor = theme === 'dark' ? '#00f5c4' : 'white';
  const { roomId, roomName, userId, username, gameMode, timeHorizon: roomTimeHorizon } = location.state || {};
  const isInfiniteMode = gameMode === 'infinite';
  const [activeTab, setActiveTab] = useState(isInfiniteMode ? 'best_infinite' : `consistency_${roomTimeHorizon ?? 10}`);
  const [rankingData, setRankingData] = useState([]);
  const [userGameData, setUserGameData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  console.log('location.state:', location.state);
  console.log('roomId:', roomId);
  console.log('user:', user);

  // Get the effective user ID and username, using values from either props or location state
  const effectiveUserId = user?.id || userId;
  const effectiveUsername = user?.username || username;
  
  
  const fetchRankingData = useCallback(() => {
    // Don't fetch if we don't have the required data
    if (!roomId || !effectiveUserId) {
      console.error('Missing required data:', { roomId, effectiveUserId });
      return;
    }

    const parts = activeTab.split('_');
    const tabType = parts[0];
    const timeHorizon = parts[1];
    let apiEndpoint;
    const isLocalDev = backendUrl.includes('localhost') || backendUrl.includes('127.0.0.1');
    const secureBackendUrl = isLocalDev ? backendUrl : backendUrl.replace('http://', 'https://');
    console.log('Inside fetchRankingData');
    console.log('secureBackendUrl:', secureBackendUrl);
    if (tabType === 'consistency') {
      apiEndpoint = `${secureBackendUrl}/room_consistency_leaderboard/${roomId}/${timeHorizon}?page=${page}`;
    } else if (tabType === 'best' && timeHorizon === 'infinite') {
      apiEndpoint = `${secureBackendUrl}/room_leaderboard_infinite/${roomId}?page=${page}`;
    } else if (tabType === 'best') {
      apiEndpoint = `${secureBackendUrl}/room_leaderboard/${roomId}/${timeHorizon}?page=${page}`;
    } else if (tabType === 'user' && timeHorizon === 'infinite') {
      apiEndpoint = `${secureBackendUrl}/user_games_infinite?user_id=${effectiveUserId}&room_id=${roomId}&page=${page}`;
    } else {
      apiEndpoint = `${secureBackendUrl}/user_games/${timeHorizon}?user_id=${effectiveUserId}&room_id=${roomId}&page=${page}`;
    }
    
    setLoading(true);
    axios.get(apiEndpoint)
      .then(response => {
        const updatedData = response.data.map(item => ({
          ...item,
          username: item.username || effectiveUsername
        }));
        if (tabType === 'best' || tabType === 'consistency') {
          setRankingData(prevData => page === 1 ? updatedData : [...prevData, ...updatedData]);
        } else {
          setUserGameData(prevData => page === 1 ? updatedData : [...prevData, ...updatedData]);
        }
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching data:', error);
        console.error('Attempted URL:', apiEndpoint);  // Add this line
        setLoading(false);
      });
  }, [activeTab, page, roomId, effectiveUserId, effectiveUsername]);

  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  useEffect(() => {
    fetchRankingData();
  }, [page, activeTab, fetchRankingData]);

  const loadMore = () => {
    setPage(prevPage => prevPage + 1);
  };

  const renderTable = (data) => (
    <div className="ranking-table-container">
      <table>
        <thead>
          <tr>
            <th>Rank</th>
            <th>Username</th>
            <th>Mean Absolute Error (ms)</th>
            <th>Average Difference (ms)</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr
              key={index}
              style={item.username === effectiveUsername ? { backgroundColor: highlightBg, color: highlightColor } : {}}
            >
              <td>{index + 1}</td>
              <td>{item.username}</td>
              <td>{item.mean_absolute_error?.toFixed(3) ?? 'N/A'}</td>
              <td>{item.average_difference?.toFixed(3) ?? 'N/A'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderInfiniteTable = (data) => (
    <div className="ranking-table-container">
      <table>
        <thead>
          <tr>
            <th>Rank</th>
            <th>Username</th>
            <th>Clicks Achieved</th>
            <th>Mean Absolute Error (ms)</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr
              key={index}
              style={item.username === effectiveUsername ? { backgroundColor: highlightBg, color: highlightColor } : {}}
            >
              <td>{index + 1}</td>
              <td>{item.username}</td>
              <td>{item.click_count ?? 'N/A'}</td>
              <td>{item.mean_absolute_error?.toFixed(3) ?? 'N/A'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderConsistencyTable = (data) => (
    <div className="ranking-table-container">
      <table>
        <thead>
          <tr>
            <th>Rank</th>
            <th>Username</th>
            <th>Consistency Score</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr 
              key={index}
              style={item.username === effectiveUsername ? { backgroundColor: highlightBg, color: highlightColor } : {}}
            >
              <td>{index + 1}</td>
              <td>{item.username}</td>
              <td>{item.ci_97_5_absolute_error?.toFixed(3) ?? 'N/A'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // If we don't have essential data, show an error
  if (!roomId || !effectiveUserId) {
    return (
      <div className="rankings-page">
        <h2>Error: Missing required data</h2>
        <p>Please return to the game and try again.</p>
      </div>
    );
  }

  const renderActiveTable = () => {
    if (activeTab.startsWith('consistency')) return renderConsistencyTable(rankingData);
    if (activeTab === 'best_infinite') return renderInfiniteTable(rankingData);
    if (activeTab === 'user_infinite') return renderInfiniteTable(userGameData);
    if (activeTab.startsWith('user')) return renderTable(userGameData);
    return renderTable(rankingData);
  };

  return (
    <div className="rankings-page">
      <DropdownMenu user={{ id: effectiveUserId, username: effectiveUsername }} />
      <h2>Room: {roomName}</h2>
      <nav className="tab-buttons">
        {isInfiniteMode ? (
          <>
            <button onClick={() => setActiveTab('best_infinite')} className={activeTab === 'best_infinite' ? 'active' : ''}>Best Games (1s Life)</button>
            <button onClick={() => setActiveTab('user_infinite')} className={activeTab === 'user_infinite' ? 'active' : ''}>Your Games (1s Life)</button>
          </>
        ) : (
          <>
            <button onClick={() => setActiveTab(`consistency_${roomTimeHorizon}`)} className={activeTab === `consistency_${roomTimeHorizon}` ? 'active' : ''}>Consistency Leaderboard {roomTimeHorizon}s</button>
            <button onClick={() => setActiveTab(`best_${roomTimeHorizon}`)} className={activeTab === `best_${roomTimeHorizon}` ? 'active' : ''}>Best Games {roomTimeHorizon}s</button>
            <button onClick={() => setActiveTab(`user_${roomTimeHorizon}`)} className={activeTab === `user_${roomTimeHorizon}` ? 'active' : ''}>Your {roomTimeHorizon}s</button>
          </>
        )}
      </nav>
      <div className="rankings-title">Rankings</div>
      <div className="rankings-content">
        <div className="table-container">
          {renderActiveTable()}
        </div>
        {loading && <p>Loading...</p>}
        <button className="load-more-button" onClick={() => navigate(-1)}>
          Return to Game
        </button>
        {!loading && rankingData.length > 0 && (
          <button className="load-more-button" onClick={loadMore}>
            Load More Rankings
          </button>
        )}
      </div>
    </div>
  );
};

export default RankingsPage;